import { t as tr } from "@/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Coins, Loader2, Plus, Wand2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { generateSubjects } from "@/lib/generate";
import { useAuth } from "@/context/AuthContext";

export const ThemeGenerator = ({ credits, onPages }) => {
  const { setCredits } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [themeId, setThemeId] = useState("landmarks-world");
  const [custom, setCustom] = useState("");
  const [age, setAge] = useState("6-8");
  const [subjects, setSubjects] = useState([]);
  const [planning, setPlanning] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCatalog(r.data);
      setSubjects(r.data.themes[0].subjects.slice(0, 4));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch catalog once on mount
  }, []);

  const pickTheme = (t) => {
    setThemeId(t.id);
    setCustom("");
    setSubjects(t.subjects.slice(0, 4));
  };

  const plan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post("/themes/suggest", { theme: custom, age_group: age, count: 4 });
      setSubjects(data.subjects);
      setThemeId(null);
      if (!data.subjects.length) toast.error("No safe subjects found — try another theme.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlanning(false);
    }
  };

  const themeTitle = custom.trim() || catalog?.themes.find((t) => t.id === themeId)?.title || "Themed Packet";

  const generateAll = async () => {
    const made = await generateSubjects(subjects, age, {
      onCredits: setCredits,
      onProgress: setProgress,
      onError: (s, msg) => toast.error(`${s}: ${msg}`),
    });
    setProgress(null);
    if (made.length) {
      onPages(made, themeTitle);
      toast.success(`${made.length} pages added to your packet`);
    }
  };

  if (!catalog) return null;
  const allThemes = catalog.themes.find((t) => t.id === themeId)?.subjects || [];
  const extras = allThemes.filter((s) => !subjects.includes(s));

  return (
    <section className="bg-amber-50/60 rounded-3xl border-2 border-amber-200/60 p-6 sm:p-8 space-y-6" data-testid="theme-generator">
      <h2 className="font-display text-2xl font-semibold">{tr("1_generate_a_themed_set")}</h2>
      <div className="flex flex-wrap gap-2" data-testid="theme-picker">
        {catalog.themes.map((t) => (
          <button key={t.id} onClick={() => pickTheme(t)} data-testid={`theme-${t.id}`}
            className={`chip ${themeId === t.id ? "bg-[#1E293B] text-white border-[#1E293B]" : "bg-white border-slate-200"}`}>{t.title}</button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={tr("or_type_your_own_theme_e_g_rainforest_an")} maxLength={80}
          data-testid="custom-theme-input" className="flex-1 rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none focus:border-[#FF5252] bg-white" />
        <div className="flex gap-2">
          {catalog.age_groups.map((g) => (
            <button key={g.id} onClick={() => setAge(g.id)} data-testid={`theme-age-${g.id}`}
              className={`chip ${age === g.id ? "bg-[#4ECDC4] border-[#1E293B]" : "bg-white border-slate-200"}`}>{g.id}</button>
          ))}
        </div>
        <button className="btn-pop btn-white !py-3" disabled={custom.trim().length < 2 || planning} onClick={plan} data-testid="suggest-subjects-button">
          {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Suggest pages
        </button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="theme-subjects">
        {subjects.map((s, i) => (
          <span key={s} className="chip bg-white border-[#FF5252] flex items-center gap-2" data-testid={`theme-subject-${i}`}>
            <Check className="w-4 h-4 text-[#FF5252]" /> {s}
            <button onClick={() => setSubjects((x) => x.filter((y) => y !== s))} data-testid={`remove-subject-${i}`}><X className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
          </span>
        ))}
        {extras.map((s) => (
          <button key={s} onClick={() => setSubjects((x) => [...x, s])} className="chip bg-white/60 border-dashed border-slate-300 text-slate-500 flex items-center gap-1" data-testid={`add-subject-${slugify(s)}`}>
            <Plus className="w-4 h-4" /> {s}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <button className="btn-pop btn-red" disabled={!subjects.length || !!progress || credits < subjects.length} onClick={generateAll} data-testid="generate-theme-set-button">
          {progress ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
          Generate {subjects.length} pages <span className="inline-flex items-center gap-1 bg-white/25 rounded-full px-2.5 py-0.5 text-sm"><Coins className="w-3.5 h-3.5" />{subjects.length}</span>
        </button>
        {progress && <span className="font-bold text-slate-700" data-testid="theme-progress">Drawing {progress.done + 1} of {progress.total}: {progress.current}...</span>}
        {!progress && credits < subjects.length && <span className="text-sm font-bold text-red-600">{tr("not_enough_credits_for_this_set")}</span>}
      </div>
    </section>
  );
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
